import React from 'react';

import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import MenuItem from '@mui/material/MenuItem';

import Record from 'ui/Record/Record';
import EntitiesDetailsLoader from 'ui/DetailsLoader/EntitiesDetailsLoader';

import { Button } from 'components/Button';
import { CenteredCircularProgress } from 'components/Progress';
import CustomModal from 'components/CustomModal';
import EntityDetailsContent from 'components/Entities/EntityDetailsContent';
import EntityDetailsHeader from 'components/Entities/EntityDetailsHeader';

import DataError from 'containers/Error/DataError';
import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import { isNotFound } from 'utils/api/errors';
import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';
import { formatLibraryId } from 'utils/formatters/libraryId';
import { canArchiveLibrary } from 'utils/roles/permissions';
import { ChoiceValue } from 'utils/library';

import { EntityModules } from 'core/constants';

import 'containers/Hazards/entityFieldConfig';
import { ARCHIVE_CONFIRMATION_MESSAGE, HAZARD_FIELD } from 'containers/Hazards/constants';
import HazardStatusPill from 'containers/Hazards/components/HazardStatusPill';

const HazardDetails: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const params = useParams<{ entityId: string }>();
  const { hazardDetails, session } = useStore();
  const [dialog, setDialog] = React.useState<'archive' | 'in-use' | null>(null);
  const [busy, setBusy] = React.useState(false);

  const canArchive = canArchiveLibrary(session.user) && !session.user?.readOnly;

  const setArchived = async (archived: boolean): Promise<void> => {
    setBusy(true);
    await hazardDetails.setArchived(archived);
    setBusy(false);
    setDialog(null);
  };

  return (
    <EntitiesDetailsLoader params={params} routeParam="entityId" store={hazardDetails}>
      {({ isLoaded, error, reload }): React.ReactElement => {
        if (!isLoaded) {
          return <CenteredCircularProgress />;
        }
        if (error) {
          return isNotFound(error) ? <NotFound /> : <DataError onReload={reload} />;
        }
        if (hazardDetails.details === undefined) {
          return <NotFound />;
        }

        const { details } = hazardDetails;
        const valueOf = (key: string): unknown => details.entity[key]?.value;
        const categoryLabel = (valueOf(HAZARD_FIELD.category) as ChoiceValue)?.[0]?.value ?? '-';
        const hazardId = formatLibraryId(EntityModules.Hazards, valueOf(HAZARD_FIELD.id) as number | null);
        const archived = Boolean(details.archivedAt);

        return (
          <>
            <Record
              navBarHeight={navBarHeight}
              Header={
                <EntityDetailsHeader
                  store={hazardDetails}
                  title={hazardDetails.getName(details)}
                  menuTestId="hazard-record-menu"
                  breadcrumbs={[
                    { label: 'Hazard library', link: hazardDetails.path },
                    { label: hazardId, link: hazardDetails.detailsPath(details.uuid) },
                  ]}
                  onDeleteFailure={() => {
                    if (!hazardDetails.deleteBlockedReason) {
                      return false;
                    }
                    setDialog('in-use');
                    return true;
                  }}
                  menuItems={
                    canArchive
                      ? (close) =>
                          archived ? (
                            <MenuItem
                              data-testid="hazard-unarchive"
                              onClick={() => {
                                close();
                                setArchived(false);
                              }}
                            >
                              Unarchive
                            </MenuItem>
                          ) : (
                            <MenuItem
                              data-testid="hazard-archive"
                              onClick={() => {
                                close();
                                setDialog('archive');
                              }}
                            >
                              Archive
                            </MenuItem>
                          )
                      : undefined
                  }
                  columns={[
                    { name: 'ID', value: <p>{hazardId}</p> },
                    { name: 'Status', value: <HazardStatusPill archived={archived} /> },
                    { name: 'Category', value: <p>{categoryLabel}</p> },
                  ]}
                />
              }
              Content={(onError) => <EntityDetailsContent onError={onError} store={hazardDetails} />}
            />
            <CustomModal
              openModal={dialog === 'archive'}
              onClose={() => setDialog(null)}
              modalTitle="Archive this hazard?"
              modalDescription={ARCHIVE_CONFIRMATION_MESSAGE}
              cancelButton={
                <Button variant="outlined" size="large" onClick={() => setDialog(null)}>
                  Cancel
                </Button>
              }
              submitButton={
                <Button variant="contained" size="large" disabled={busy} onClick={() => setArchived(true)}>
                  Archive
                </Button>
              }
            />
            <CustomModal
              openModal={dialog === 'in-use'}
              onClose={() => setDialog(null)}
              modalTitle="This hazard is in use"
              modalDescription={`${hazardDetails.deleteBlockedReason ?? ''} Archive it instead, so it stays where it is used but cannot be added again.`}
              cancelButton={
                <Button variant="outlined" size="large" onClick={() => setDialog(null)}>
                  Cancel
                </Button>
              }
              submitButton={
                <Button variant="contained" size="large" disabled={busy || archived} onClick={() => setArchived(true)}>
                  Archive
                </Button>
              }
            />
          </>
        );
      }}
    </EntitiesDetailsLoader>
  );
};

export default observer(HazardDetails);
